

















































































/* global NodeJS */
import { Component, Vue, Watch } from 'vue-property-decorator';
import Spinner from '@/components/Spinner.vue';
import Turntable from '@/components/Turntable.vue';
import Status, { StatusData } from '@/components/Status.vue';
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import { Localization } from '@/services/localization';
import { GiveawayResponse } from '@/constants/interfaces';
import { brandContext, Locale } from '@/constants/locales';
import { Market } from '@avp/context-claims';
const Consumption = () => {
  switch (brandContext) {
    case 'Cupra':
      return import('@/components/brands/consumption/ConsumptionCupra.vue');
    case 'Volkswagen':
      return import('@/components/brands/consumption/ConsumptionVolkswagen.vue');
    default:
      return import('@/components/brands/consumption/ConsumptionAudi.vue');
  }
};

const initialState: GiveawayResponse = {
  id: '',
  assets: [],
  marketingCode: '',
  dealerLink: '',
  dealerAddress: '',
  giveawayStatus: 'ACCEPTED',
  market: Market.DEU,
  locale: Locale.de_DE,
  carModel: '',
  createdAt: '',
  prString: '',
  updatedAt: '',
  renderCameraFlight: false,
  consumptionAndEmissionData: {
    fuels: {}
  },
  cameraFlightVideo: ''
};

@Component({
  components: {
    Status,
    Spinner,
    Turntable,
    Footer,
    Consumption,
    Header
  }
})
export default class Giveaway extends Vue {
  private giveawayResponse: GiveawayResponse = { ...initialState };
  private displayRenderHint = false;
  private pollInterval!: NodeJS.Timer;

  get brandContext(): string {
    return brandContext;
  }

  locale(translationKey: string, parameters?: Record<string, string>) {
    return this.$t(`giveaway.${translationKey}`, parameters);
  }

  @Watch('$route.params.id', { immediate: true })
  async loadGiveaway() {
    this.giveawayResponse = { ...initialState };
    this.displayRenderHint = false;
    setTimeout((): void => {
      this.displayRenderHint = true;
    }, 8000);
    try {
      this.giveawayResponse = await this.$digitalGiveawayClient.getOrder(this.$route.params.id);
      // We are polling the backend every 30 seconds until the camera flight video is available
      if (
        (this.giveawayResponse.renderCameraFlight && !this.giveawayResponse.cameraFlightVideo) ||
        !this.processingCompleted
      ) {
        this.pollInterval = setInterval(async () => {
          console.log('POLLING THE BACKEND');
          this.giveawayResponse = await this.$digitalGiveawayClient.getOrder(this.$route.params.id);
          if (
            (!this.giveawayResponse.renderCameraFlight && this.processingCompleted) ||
            (this.giveawayResponse.renderCameraFlight && this.giveawayResponse.cameraFlightVideo)
          ) {
            console.log('CLEARING THE POLLING INTERVAL');
            clearInterval(this.pollInterval);
          }
        }, 30000);
        setTimeout(() => {
          clearInterval(this.pollInterval);
        }, 36000000);
      }
      // eslint-disable-next-line
    } catch (error: any) {
      switch (error.name) {
        case 'HTTPError':
          this.giveawayResponse = await this.$digitalGiveawayClient.getOrder(this.$route.params.id);
          break;
        case 'NotFoundError':
        default:
          this.giveawayResponse.giveawayStatus = 'ABORTED';
          return;
      }
    }
    const i18nLocaleFromResponse = Localization.getI18nLocale(
      this.giveawayResponse.locale as Locale,
      this.giveawayResponse.market as Market
    );
    this.$i18n.locale = i18nLocaleFromResponse;
    this.$i18n.fallbackLocale = Locale.en;

    await new Promise((resolve) => setTimeout(resolve, 30000));
  }

  get showStatus() {
    return !this.localeValid || !this.imagesAvailable;
  }

  get localeValid() {
    // ensure that we have one of the configured locales and thus display a proper legal text
    return Localization.isI18nLocaleValid(this.$i18n.locale);
  }

  get processingCompleted() {
    return ['FINISHED', 'EXPIRED', 'ABORTED'].includes(this.giveawayResponse.giveawayStatus);
  }
  get imagesPartiallyAvailable(): boolean {
    return this.localeValid && !this.processingCompleted && !!this.giveawayResponse.assets?.length;
  }
  get imagesAvailable(): boolean {
    return this.localeValid && !!this.giveawayResponse.assets?.length;
  }
  get vmsVideosAvailable(): boolean {
    return this.localeValid && !!this.giveawayResponse.vmsVideos?.length;
  }
  get cameraFlightVideoAvailable(): boolean {
    return this.localeValid && !!this.giveawayResponse.cameraFlightVideo;
  }
  get loadingMoreStatusProps(): StatusData {
    return {
      message: this.locale('preparingMoreImages.message', { carModel: this.carModel }).toString(),
      spinner: true
    };
  }

  get statusProps(): StatusData | undefined {
    const status = this.giveawayResponse.giveawayStatus;

    if (status === 'ACCEPTED') {
      return {
        title: this.locale('preparingMoreImages.title').toString(),
        message: this.displayRenderHint
          ? this.locale('preparingMoreImages.message', { carModel: this.carModel }).toString()
          : undefined,
        spinner: true
      };
    } else if (status === 'EXPIRED') {
      return {
        title: this.locale('giveawayExpired.title').toString(),
        message: this.locale('giveawayExpired.message').toString(),
        spinner: false
      };
    } else if (status === 'ABORTED' || !this.localeValid) {
      return {
        title: this.locale('somethingWentWrong.title').toString(),
        message: this.locale('somethingWentWrong.message', { carModel: this.carModel }).toString(),
        spinner: false
      };
    }
    return undefined;
  }

  sectionUrls(section: string): Array<string> {
    return (
      this.giveawayResponse.assets
        ?.filter((asset) => asset.metadata?.section === section)
        .sort((a, b) => (a.metadata.index || 0) - (b.metadata.index || 0))
        .map((asset) => asset.url) || []
    );
  }

  get cameraFlightVideo() {
    return this.giveawayResponse.cameraFlightVideo;
  }

  get carModel() {
    return this.giveawayResponse.carModel || brandContext;
  }

  get consumptionProps() {
    return {
      consumptionAndEmissionData: this.giveawayResponse.consumptionAndEmissionData,
      createdAt: this.giveawayResponse.createdAt
    };
  }

  get exteriorShots() {
    return this.sectionUrls('exterior');
  }

  get exteriorNightShots() {
    return this.sectionUrls('exterior-night');
  }

  get interiorShots() {
    return this.sectionUrls('interior');
  }

  get turntableImageUrls() {
    return this.sectionUrls('turntable');
  }

  get vmsVideoLinks() {
    return this.giveawayResponse.vmsVideos?.map((vmsVideo) => vmsVideo.resolutions);
  }

  get marketingCode() {
    return this.giveawayResponse.marketingCode !== 'VIN' ? this.giveawayResponse.marketingCode : '';
  }
}
